import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Link } from "react-router-dom"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { useEffect } from "react"
import { Helmet } from "react-helmet-async"
import { FuseguLogo } from "@/components/FuseguLogo"
import { ArrowLeft, CheckCircle, XCircle, DollarSign, Zap, Shield } from "lucide-react"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

export default function SiteLockAlternative() {
  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  return (
    <>
      <Helmet>
        <title>SiteLock Alternative - Multi-Site Scanning for Agencies | Fusegu</title>
        <meta
          name="description"
          content="Looking for a SiteLock alternative for agencies? Fusegu offers unlimited site scanning at $49/month vs SiteLock's $20-45/month per site. White-label reports included. Try free."
        />
        <meta property="og:title" content="SiteLock Alternative - Unlimited Sites, No Per-Site Fees" />
        <meta property="og:description" content="Switch from SiteLock to Fusegu for multi-site agencies. Scan unlimited sites with white-label reports at one flat rate." />
        <meta property="og:url" content="https://fusegusecurity.com/sitelock-alternative" />
        <meta name="keywords" content="SiteLock alternative, website security, vulnerability scanning, transparent pricing, affordable security" />
      </Helmet>
      <div className="flex min-h-screen w-full flex-col">
        {/* Header */}
        <header className="sticky top-0 flex h-16 items-center gap-4 border-b bg-background px-4 md:px-6 z-50">
          <div className="flex items-center justify-between w-full">
            <Link to="/" className="flex items-center gap-2">
              <FuseguLogo className="h-6 w-6 flex-shrink-0" />
              <span className="text-lg font-bold">Fusegu</span>
            </Link>
            <div className="flex items-center gap-4">
              <Link to="/login">
                <Button variant="ghost">Sign In</Button>
              </Link>
              <Link to="/register">
                <Button>Start Free Trial</Button>
              </Link>
            </div>
          </div>
        </header>

        <main className="flex-1 py-12 px-4 md:px-8 bg-gradient-to-br from-slate-50 to-blue-50">
          <div className="max-w-4xl mx-auto">
            {/* Back to home link */}
            <Link to="/" className="inline-flex items-center gap-2 text-blue-600 hover:text-blue-700 mb-6">
              <ArrowLeft className="h-4 w-4" />
              <span>Back to Home</span>
            </Link>

            {/* Article Header */}
            <div className="mb-8">
              <h1 className="text-4xl md:text-5xl font-bold mb-4 leading-tight">
                SiteLock Alternative - Unlimited Sites for Agencies
              </h1>
              <p className="text-xl text-gray-700 leading-relaxed">
                Looking for a SiteLock alternative for agencies? Fusegu offers comprehensive security
                scanning for unlimited sites at one flat rate - perfect for agencies managing multiple client websites.
              </p>
            </div>

            {/* Quick Value Props */}
            <div className="grid md:grid-cols-3 gap-6 mb-12">
              <Card className="border-2 border-green-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <DollarSign className="h-6 w-6 text-green-600" />
                    <CardTitle className="text-lg">Multi-Site Value</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-green-600">$49/mo</p>
                  <p className="text-sm text-gray-600">vs $20-45/mo per site</p>
                  <p className="text-sm font-semibold mt-2">Unlimited sites</p>
                </CardContent>
              </Card>

              <Card className="border-2 border-blue-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <Shield className="h-6 w-6 text-blue-600" />
                    <CardTitle className="text-lg">Unlimited Sites</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-blue-600">50/day</p>
                  <p className="text-sm text-gray-600">1,500 scans per month</p>
                  <p className="text-sm font-semibold mt-2">One flat rate</p>
                </CardContent>
              </Card>

              <Card className="border-2 border-purple-200">
                <CardHeader className="pb-3">
                  <div className="flex items-center gap-2">
                    <Zap className="h-6 w-6 text-purple-600" />
                    <CardTitle className="text-lg">Start Instantly</CardTitle>
                  </div>
                </CardHeader>
                <CardContent>
                  <p className="text-2xl font-bold text-purple-600">5 min</p>
                  <p className="text-sm text-gray-600">Sign up and scan</p>
                  <p className="text-sm font-semibold mt-2">No sales process</p>
                </CardContent>
              </Card>
            </div>

            {/* Why Teams Switch */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="text-2xl">Why Teams Switch from SiteLock to Fusegu</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">Better Value for Multi-Site Agencies</p>
                    <p className="text-gray-600">SiteLock charges $20-45/month per site. Fusegu is $49/month for unlimited sites - huge savings once you manage 3+ client sites.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">No Per-Site Fees</p>
                    <p className="text-gray-600">Pay one flat rate, scan all your clients. Scale your agency without increasing security costs every time you add a client.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">More Comprehensive Scanning</p>
                    <p className="text-gray-600">10+ industry-standard scanners (OWASP ZAP, SSL Labs, Nuclei) providing broader coverage than SiteLock's basic plan.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">Modern Security Scanning</p>
                    <p className="text-gray-600">10+ industry-standard scanners (OWASP ZAP, SSL Labs, Nuclei) vs SiteLock's proprietary tools.</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-6 w-6 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-semibold">White-Label Reports Included</p>
                    <p className="text-gray-600">Professional client reports with your branding. No extra fees, included in base plan.</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Comparison Table */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="text-2xl">SiteLock vs Fusegu</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-[200px]">Feature</TableHead>
                        <TableHead>SiteLock</TableHead>
                        <TableHead>Fusegu</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      <TableRow>
                        <TableCell className="font-medium">Basic Plan</TableCell>
                        <TableCell>$19.99/month per site</TableCell>
                        <TableCell className="font-bold text-green-600">$49/month unlimited sites</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Pro Plan</TableCell>
                        <TableCell>$29.99/month per site</TableCell>
                        <TableCell className="font-bold text-green-600">Included (1 plan)</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Business Plan</TableCell>
                        <TableCell>$44.99/month per site</TableCell>
                        <TableCell className="font-bold text-green-600">Included (1 plan)</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Sales Process</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-600" />
                            <span className="text-sm">Required sales call</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="font-bold">Sign up instantly</span>
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Sites Included</TableCell>
                        <TableCell>Typically 1 per plan</TableCell>
                        <TableCell className="font-bold text-green-600">Unlimited (50 scans/day)</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">White-label Reports</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-600" />
                            <span className="text-sm">Not standard</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="font-bold">Included</span>
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Security Scanners</TableCell>
                        <TableCell>Proprietary SiteLock tools</TableCell>
                        <TableCell className="font-bold">10+ tools (OWASP ZAP, Nuclei, SSL Labs, etc.)</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">SOC2 Compliance</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-600" />
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="font-bold">Yes</span>
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">CDN</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="text-sm">Available</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-600" />
                          </div>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell className="font-medium">Malware Removal</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <CheckCircle className="h-4 w-4 text-green-600" />
                            <span className="text-sm">Available</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <XCircle className="h-4 w-4 text-red-600" />
                            <span>Detection only</span>
                          </div>
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </CardContent>
            </Card>

            {/* When SiteLock is Better */}
            <Card className="mb-8 border-2 border-orange-200">
              <CardHeader>
                <CardTitle className="text-2xl flex items-center gap-2">
                  <span>When SiteLock is Better</span>
                  <Badge variant="outline" className="ml-2">Honest Comparison</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-gray-700">SiteLock may be better for:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Complete managed security</strong> - If you want someone else to handle everything</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Malware removal services</strong> - SiteLock can clean infected sites</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                    <span><strong>CDN needs</strong> - Bundled with some SiteLock plans</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-orange-600 mt-0.5 flex-shrink-0" />
                    <span><strong>You prefer phone support</strong> - SiteLock offers dedicated phone support</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* When Fusegu is Better */}
            <Card className="mb-8 border-2 border-green-200">
              <CardHeader>
                <CardTitle className="text-2xl text-green-600">When Fusegu is Better</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <p className="text-gray-700">Fusegu is perfect for:</p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Agencies managing multiple sites</strong> - Unlimited scanning vs per-site fees</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Teams wanting transparent pricing</strong> - See exact costs upfront, no sales calls</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Self-service preference</strong> - Sign up and start scanning immediately</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Budget-conscious organizations</strong> - Save thousands compared to SiteLock</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Professional client reporting</strong> - White-labeled reports with your branding</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                    <span><strong>Vulnerability detection focus</strong> - You handle remediation yourself</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* Why Agencies Choose Fusegu */}
            <Card className="mb-8 border-2 border-blue-200">
              <CardHeader>
                <CardTitle className="text-2xl">Why Agencies Choose Fusegu Over SiteLock</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <p className="font-semibold mb-2">1. "Better economics for agencies"</p>
                  <p className="text-gray-700 mb-1">SiteLock's per-site pricing ($20-45/month) adds up quickly for agencies.</p>
                  <p className="text-green-700 font-semibold">✓ Fusegu: $49/month for unlimited sites. Cost-effective at scale.</p>
                </div>
                <div>
                  <p className="font-semibold mb-2">2. "White-label client reporting"</p>
                  <p className="text-gray-700 mb-1">SiteLock doesn't offer white-labeled reports for agencies.</p>
                  <p className="text-green-700 font-semibold">✓ Fusegu: Professional PDF reports with your branding included.</p>
                </div>
                <div>
                  <p className="font-semibold mb-2">3. "More comprehensive scanning"</p>
                  <p className="text-gray-700 mb-1">SiteLock Basic plan has limited scanning capabilities.</p>
                  <p className="text-green-700 font-semibold">✓ Fusegu: 10+ industry-standard scanners including OWASP ZAP, SSL Labs, Nuclei.</p>
                </div>
                <div>
                  <p className="font-semibold mb-2">4. "Centralized multi-site management"</p>
                  <p className="text-gray-700 mb-1">Managing SiteLock across many client sites can be cumbersome.</p>
                  <p className="text-green-700 font-semibold">✓ Fusegu: One dashboard to manage security for all client sites.</p>
                </div>
                <div>
                  <p className="font-semibold mb-2">5. "On-demand scanning"</p>
                  <p className="text-gray-700 mb-1">SiteLock runs on a schedule - not always when you need it.</p>
                  <p className="text-green-700 font-semibold">✓ Fusegu: Scan anytime in 5-10 minutes. Perfect for pre-launch checks.</p>
                </div>
              </CardContent>
            </Card>

            {/* Pricing Comparison */}
            <Card className="mb-8 border-2 border-blue-200">
              <CardHeader>
                <CardTitle className="text-2xl">Estimated Cost Comparison</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-6">
                  <div>
                    <p className="text-lg font-semibold mb-4">Annual Cost for 10 Client Sites (Basic Plan):</p>
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div className="p-4 bg-gray-100 rounded-lg">
                        <p className="text-sm text-gray-600">SiteLock Basic</p>
                        <p className="text-3xl font-bold">$2,398/year</p>
                        <p className="text-sm text-gray-600">10 sites × $19.99/mo × 12</p>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg border-2 border-green-300">
                        <p className="text-sm text-green-700">Fusegu</p>
                        <p className="text-3xl font-bold text-green-600">$588/year</p>
                        <p className="text-sm text-green-700">Unlimited sites</p>
                      </div>
                    </div>
                    <div className="p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border-2 border-green-200">
                      <p className="text-2xl font-bold text-green-700 mb-2">You Save: $1,810/year</p>
                      <p className="text-gray-700">Significant savings while getting more comprehensive scanning and white-label reports.</p>
                    </div>
                  </div>

                  <div>
                    <p className="text-lg font-semibold mb-4">Annual Cost for 10 Client Sites (Pro Plan):</p>
                    <div className="grid md:grid-cols-2 gap-4 mb-4">
                      <div className="p-4 bg-gray-100 rounded-lg">
                        <p className="text-sm text-gray-600">SiteLock Pro</p>
                        <p className="text-3xl font-bold">$3,598/year</p>
                        <p className="text-sm text-gray-600">10 sites × $29.99/mo × 12</p>
                      </div>
                      <div className="p-4 bg-green-50 rounded-lg border-2 border-green-300">
                        <p className="text-sm text-green-700">Fusegu</p>
                        <p className="text-3xl font-bold text-green-600">$588/year</p>
                        <p className="text-sm text-green-700">Unlimited sites</p>
                      </div>
                    </div>
                    <div className="p-4 bg-gradient-to-r from-green-50 to-blue-50 rounded-lg border-2 border-green-200">
                      <p className="text-2xl font-bold text-green-700 mb-2">You Save: $3,010/year</p>
                      <p className="text-gray-700">Dramatic savings - enough to invest in other tools or hire additional help.</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* FAQ Section */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="text-2xl">Frequently Asked Questions</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div>
                  <p className="font-semibold text-lg mb-2">How much does SiteLock actually cost?</p>
                  <p className="text-gray-700">
                    SiteLock doesn't publish pricing publicly. Based on user reports, plans typically range from
                    $200-500+/month per site, with variation based on features and negotiation. Fusegu is transparently
                    $49/month for unlimited sites.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-lg mb-2">Can Fusegu replace SiteLock completely?</p>
                  <p className="text-gray-700">
                    For vulnerability scanning and detection, yes. However, if you need malware removal services or
                    managed security response, you may need additional tools. Fusegu focuses on comprehensive
                    scanning and reporting.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-lg mb-2">Do I need to talk to sales to use Fusegu?</p>
                  <p className="text-gray-700">
                    No! Sign up instantly at fusegusecurity.com, no sales call required. Start scanning in under
                    5 minutes. We only talk to customers who reach out to us.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-lg mb-2">What if I'm currently under contract with SiteLock?</p>
                  <p className="text-gray-700">
                    You can start using Fusegu alongside SiteLock during your contract period. Many agencies run
                    both in parallel before fully switching. This lets you compare results and validate the switch.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-lg mb-2">How accurate is Fusegu compared to SiteLock?</p>
                  <p className="text-gray-700">
                    Fusegu uses 10+ industry-standard security scanners (OWASP ZAP, SSL Labs, Nuclei, Nmap) that
                    are widely trusted in the security community. We provide detailed, actionable findings without
                    exaggeration or scare tactics.
                  </p>
                </div>
                <div>
                  <p className="font-semibold text-lg mb-2">Can I try Fusegu before committing?</p>
                  <p className="text-gray-700">
                    Yes! Start with a free trial - no credit card required. Scan your sites and compare the results
                    to SiteLock. Most agencies are convinced after the first scan.
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* CTA Section */}
            <Card className="mb-8 bg-gradient-to-br from-blue-600 to-purple-700 text-white border-none">
              <CardContent className="p-8 text-center">
                <h2 className="text-3xl font-bold mb-4">Try Fusegu Free - No Credit Card, No Sales Call</h2>
                <p className="text-xl mb-6 text-blue-50">
                  Experience transparent pricing and comprehensive scanning. Start in 5 minutes.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/register">
                    <Button size="lg" className="bg-white text-blue-600 hover:bg-blue-50 font-semibold px-8">
                      Start Free Trial
                    </Button>
                  </Link>
                  <Link to="/">
                    <Button size="lg" className="bg-blue-700 text-white hover:bg-blue-800 font-semibold px-8 border-2 border-white">
                      View Demo
                    </Button>
                  </Link>
                </div>
                <p className="text-sm mt-4 text-blue-100">
                  Join agencies who switched from SiteLock and never looked back
                </p>
              </CardContent>
            </Card>

          </div>
        </main>

        {/* Footer */}
        <footer className="border-t py-8 px-4 md:px-8 bg-white">
          <div className="max-w-4xl mx-auto text-center text-sm text-gray-600">
            <p className="mb-4">Fusegu Security | fusegusecurity.com</p>
            <div className="flex justify-center gap-6 flex-wrap">
              <Link to="/privacy" className="hover:text-blue-600">Privacy Policy</Link>
              <Link to="/terms" className="hover:text-blue-600">Terms of Service</Link>
              <Link to="/blogs" className="hover:text-blue-600">Blog</Link>
            </div>
          </div>
        </footer>
      </div>
    </>
  )
}
